/**
 * Security utilities for frontend AppAgenda
 * Provides client-side security validations and protections
 */

// Rate limiting for API calls
interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private requests: Map<string, RateLimitEntry> = new Map();
  private readonly maxRequests: number;
  private readonly windowMs: number;

  constructor(maxRequests: number = 30, windowMs: number = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  canMakeRequest(endpoint: string): boolean {
    const now = Date.now();
    const key = endpoint;
    const entry = this.requests.get(key);

    if (!entry || now > entry.resetTime) {
      // Reset or create new entry
      this.requests.set(key, {
        count: 1,
        resetTime: now + this.windowMs
      });
      return true;
    }

    if (entry.count >= this.maxRequests) {
      return false;
    }

    entry.count++;
    return true;
  }

  getRemainingRequests(endpoint: string): number {
    const now = Date.now();
    const entry = this.requests.get(endpoint);

    if (!entry || now > entry.resetTime) {
      return this.maxRequests;
    }

    return Math.max(0, this.maxRequests - entry.count);
  }

  getResetTime(endpoint: string): number | null {
    const entry = this.requests.get(endpoint);
    return entry ? entry.resetTime : null;
  }
}

// CSRF Protection
class CSRFProtection {
  private static token: string | null = null;

  static generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    this.token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    return this.token;
  }

  static getToken(): string | null {
    return this.token;
  }

  static setToken(token: string): void {
    this.token = token;
  }

  static validateToken(receivedToken: string): boolean {
    return this.token === receivedToken;
  }
}

// Input Sanitization
class InputSanitizer {
  static sanitize(input: string): string {
    if (!input) return input;

    return input
      .replace(/[<>]/g, '') // Remove HTML tags
      .replace(/javascript:/gi, '') // Remove JavaScript protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .replace(/['"]/g, '') // Remove quotes
      .trim();
  }

  static sanitizeEmail(email: string): string {
    return email.toLowerCase().trim();
  }

  static sanitizePhone(phone: string): string {
    return phone.replace(/[^\d+()-\s]/g, '');
  }

  static sanitizeNumeric(input: string): string {
    return input.replace(/[^\d]/g, '');
  }

  static validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('A senha deve ter pelo menos 8 caracteres');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('A senha deve conter pelo menos uma letra maiúscula');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('A senha deve conter pelo menos uma letra minúscula');
    }

    if (!/\d/.test(password)) {
      errors.push('A senha deve conter pelo menos um número');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('A senha deve conter pelo menos um caractere especial');
    }

    // Common passwords
    const commonPasswords = ['password', '123456', 'qwerty', 'admin'];
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('A senha não pode ser uma senha comum');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// XSS Protection
class XSSProtection {
  static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  static preventXSSInUrl(url: string): boolean {
    const xssPatterns = [
      /javascript:/i,
      /data:text\/html/i,
      /vbscript:/i,
      /onload=/i,
      /onerror=/i,
      /onclick=/i,
      /onmouseover=/i
    ];

    return !xssPatterns.some(pattern => pattern.test(url));
  }

  static validateUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }
}

// Session Security
class SessionSecurity {
  private static readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  private static readonly WARNING_TIMEOUT = 25 * 60 * 1000; // 25 minutes
  private static lastActivity: number = Date.now();
  private static warningTimer: NodeJS.Timeout | null = null;
  private static logoutTimer: NodeJS.Timeout | null = null;

  // Só há sessão para monitorar quando existe token real (secureStorage:
  // prefixo 'appagenda_' + 'auth_token'). Visitante anônimo em página pública
  // (landing, /para/*) NÃO deve ver aviso de inatividade nem ser deslogado.
  private static isAuthed(): boolean {
    try {
      return typeof window !== 'undefined' && !!localStorage.getItem('appagenda_auth_token');
    } catch {
      return false;
    }
  }

  static updateActivity(): void {
    SessionSecurity.lastActivity = Date.now();
    SessionSecurity.resetTimers();
  }

  static resetTimers(): void {
    if (SessionSecurity.warningTimer) clearTimeout(SessionSecurity.warningTimer);
    if (SessionSecurity.logoutTimer) clearTimeout(SessionSecurity.logoutTimer);
    SessionSecurity.warningTimer = null;
    SessionSecurity.logoutTimer = null;

    // Não arma timers para visitante anônimo (páginas públicas)
    if (!SessionSecurity.isAuthed()) return;

    SessionSecurity.warningTimer = setTimeout(() => {
      SessionSecurity.showInactivityWarning();
    }, SessionSecurity.WARNING_TIMEOUT);

    SessionSecurity.logoutTimer = setTimeout(() => {
      SessionSecurity.logout();
    }, SessionSecurity.INACTIVITY_TIMEOUT);
  }

  private static showInactivityWarning(): void {
    if (!SessionSecurity.isAuthed()) return;
    const message = 'Sua sessão expirará em 5 minutos por inatividade.';
    if (typeof window !== 'undefined') {
      alert(message);
    }
  }

  private static logout(): void {
    if (!SessionSecurity.isAuthed()) return;

    // Limpa o token real (secureStorage) + chaves legadas
    localStorage.removeItem('appagenda_auth_token');
    localStorage.removeItem('appagenda_refresh_token');
    localStorage.removeItem('appagenda_user_data');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.clear();

    if (typeof window !== 'undefined') {
      window.location.href = '/login?reason=timeout';
    }
  }

  static isSessionExpired(): boolean {
    return Date.now() - this.lastActivity > this.INACTIVITY_TIMEOUT;
  }
}

// Secure Storage
class SecureStorage {
  private static readonly PREFIX = 'appagenda_security_';
  private static readonly ENCRYPTION_KEY = 'appagenda_secure_key';

  static setItem(key: string, value: unknown, useEncryption: boolean = false): void {
    const prefixedKey = this.PREFIX + key;
    const data = JSON.stringify({
      value,
      timestamp: Date.now(),
      encrypted: useEncryption
    });

    if (useEncryption && typeof window !== 'undefined') {
      const encrypted = this.encrypt(data);
      localStorage.setItem(prefixedKey, encrypted);
    } else {
      localStorage.setItem(prefixedKey, data);
    }
  }

  static getItem<T>(key: string, maxAge?: number): T | null {
    if (typeof window === 'undefined') return null;

    const prefixedKey = this.PREFIX + key;
    const stored = localStorage.getItem(prefixedKey);

    if (!stored) return null;

    try {
      const decrypted = this.isEncrypted(stored) ? this.decrypt(stored) : stored;
      const data = JSON.parse(decrypted);

      // Check age
      if (maxAge && Date.now() - data.timestamp > maxAge) {
        this.removeItem(key);
        return null;
      }

      return data.value;
    } catch {
      this.removeItem(key);
      return null;
    }
  }

  static removeItem(key: string): void {
    if (typeof window === 'undefined') return;

    const prefixedKey = this.PREFIX + key;
    localStorage.removeItem(prefixedKey);
  }

  private static encrypt(data: string): string {
    // Simple XOR encryption (consider using Web Crypto API for production)
    const key = this.getEncryptionKey();
    let encrypted = '';

    for (let i = 0; i < data.length; i++) {
      encrypted += String.fromCharCode(
        data.charCodeAt(i) ^ key.charCodeAt(i % key.length)
      );
    }

    return btoa(encrypted);
  }

  private static decrypt(encrypted: string): string {
    const key = this.getEncryptionKey();
    const data = atob(encrypted);
    let decrypted = '';

    for (let i = 0; i < data.length; i++) {
      decrypted += String.fromCharCode(
        data.charCodeAt(i) ^ key.charCodeAt(i % key.length)
      );
    }

    return decrypted;
  }

  private static getEncryptionKey(): string {
    let key = localStorage.getItem(this.ENCRYPTION_KEY);
    if (!key) {
      key = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      localStorage.setItem(this.ENCRYPTION_KEY, key);
    }
    return key;
  }

  private static isEncrypted(data: string): boolean {
    try {
      atob(data);
      return true;
    } catch {
      return false;
    }
  }

  static cleanup(): void {
    if (typeof window === 'undefined') return;

    // Remove expired items
    const keys = Object.keys(localStorage);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    keys.forEach(key => {
      if (key.startsWith(this.PREFIX)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const decrypted = this.isEncrypted(stored) ? this.decrypt(stored) : stored;
            const data = JSON.parse(decrypted);

            if (Date.now() - data.timestamp > maxAge) {
              localStorage.removeItem(key);
            }
          } catch {
            localStorage.removeItem(key);
          }
        }
      }
    });
  }
}

// Request Security
class RequestSecurity {
  private static rateLimiter = new RateLimiter();

  static async secureRequest<T>(
    url: string,
    options: RequestInit = {},
    customRateLimit?: { maxRequests: number; windowMs: number }
  ): Promise<T> {
    // Rate limiting
    if (!this.rateLimiter.canMakeRequest(url)) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    // CSRF protection
    const csrfToken = CSRFProtection.getToken();
    if (csrfToken && options.headers) {
      (options.headers as Record<string, string>)['X-CSRF-Token'] = csrfToken;
    }

    // Add security headers
    const secureOptions: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, secureOptions);

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests. Please try again later.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Security request failed:', error);
      throw error;
    }
  }

  static validateApiResponse(data: unknown, expectedFields: string[]): boolean {
    if (!data || typeof data !== 'object') return false;

    return expectedFields.every(field => field in (data as Record<string, unknown>));
  }
}

// Error Handler with Security Context
class SecurityErrorHandler {
  static handleError(error: Error, context?: unknown): void {
    const errorData = {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    };

    // Don't log sensitive information
    const sanitizedError = this.sanitizeErrorData(errorData);

    console.error('Security error:', sanitizedError);

    // Send to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // This would integrate with your monitoring service
      // Example: Sentry.captureException(error, { extra: sanitizedError });
    }
  }

  private static sanitizeErrorData(data: unknown): unknown {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    const sanitized = { ...data as Record<string, unknown> };

    // Remove potentially sensitive fields
    const sensitiveFields = ['password', 'token', 'key', 'secret', 'auth'];

    const removeSensitiveFields = (obj: unknown): unknown => {
      if (typeof obj !== 'object' || obj === null) return obj;

      const result: Record<string, unknown> | unknown[] = Array.isArray(obj) ? [] : {};

      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();

        if (sensitiveFields.some(field => lowerKey.includes(field))) {
          result[key] = '[REDACTED]';
        } else if (typeof value === 'object') {
          result[key] = removeSensitiveFields(value);
        } else {
          result[key] = value;
        }
      }

      return result;
    };

    return removeSensitiveFields(sanitized);
  }
}

// Security monitoring
class SecurityMonitor {
  private static suspiciousActivities: Map<string, number> = new Map();

  static reportSuspiciousActivity(type: string, details: unknown): void {
    const key = `${type}:${JSON.stringify(details)}`;
    const count = this.suspiciousActivities.get(key) || 0;
    this.suspiciousActivities.set(key, count + 1);

    console.warn('Suspicious activity detected:', { type, details, count });

    if (count > 5) {
      // Report to server
      this.reportToSecurityTeam(type, details, count);
    }
  }

  private static reportToSecurityTeam(type: string, details: unknown, count: number): void {
    // In production, this would send to your security monitoring service
    console.error('Security threat detected:', { type, details, count });
  }

  static validateRequest(request: { headers?: Record<string, string> }): boolean {
    const suspiciousIndicators: string[] = [];

    // Check for missing user agent
    if (!request.headers || !request.headers['user-agent']) {
      suspiciousIndicators.push('Missing user agent');
    }

    // Check for suspicious headers
    const suspiciousHeaders = ['x-forwarded-for', 'x-real-ip'];
    const headerCount = suspiciousHeaders.filter(header =>
      request.headers && request.headers[header]
    ).length;

    if (headerCount > 2) {
      suspiciousIndicators.push('Multiple proxy headers');
    }

    if (suspiciousIndicators.length > 0) {
      this.reportSuspiciousActivity('suspicious_request', {
        indicators: suspiciousIndicators,
        headers: request.headers
      });
      return false;
    }

    return true;
  }
}

// Export all security utilities
export {
  RateLimiter,
  CSRFProtection,
  InputSanitizer,
  XSSProtection,
  SessionSecurity,
  SecureStorage,
  RequestSecurity,
  SecurityErrorHandler,
  SecurityMonitor
};

// Global security initialization
export const initializeSecurity = (): void => {
  // Initialize CSRF token
  CSRFProtection.generateToken();

  // Setup activity monitoring
  if (typeof window !== 'undefined') {
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => SessionSecurity.updateActivity(), true);
    });
  }

  // Cleanup old data
  SecureStorage.cleanup();
};

export default {
  RateLimiter,
  CSRFProtection,
  InputSanitizer,
  XSSProtection,
  SessionSecurity,
  SecureStorage,
  RequestSecurity,
  SecurityErrorHandler,
  SecurityMonitor,
  initializeSecurity
};
